// @ts-nocheck
/**
 * FX取引画面
 * 属性レート取引のUI
 */

import React, { useState, useMemo } from 'react';
import { FX_PAIRS, getPairVolatility, getRiskCategory } from '../data/fxPairs';
import {
  getAllFXPairsWithDetails,
  getFXPositionDetails,
  validateOpenFXPosition,
  openFXPosition,
  closeFXPosition,
} from '../systems/fxManager';
import { getUnlockedLeverages } from '../systems/portfolioManager';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { INVESTMENT } from '../../data/constants';
import { saveInvestmentTradeToServer } from '../../../lib/supabase';

const FXTrading = ({
  playerData,
  updatePlayerData,
  currentBattleNumber,
  marketIndex,
  marketState,
}) => {
  const isMobile = useIsMobile();
  const [selectedPair, setSelectedPair] = useState(null);
  const [tradeMode, setTradeMode] = useState('list'); // 'list' | 'trade' | 'positions'
  const [direction, setDirection] = useState('long');
  const [marginInput, setMarginInput] = useState('10000');
  const [leverage, setLeverage] = useState(1);
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const investmentData = playerData.investmentData || {};
  const gold = playerData.gold || 0;

  // ペア一覧
  const pairs = useMemo(
    () => getAllFXPairsWithDetails(investmentData, marketIndex, marketState),
    [investmentData, marketIndex, marketState]
  );

  // ポジション一覧
  const positions = useMemo(() => {
    const fxPositions = investmentData.fxPositions || [];
    return fxPositions.map((pos) =>
      getFXPositionDetails(pos, investmentData, currentBattleNumber, marketIndex, marketState)
    );
  }, [investmentData, currentBattleNumber, marketIndex, marketState]);

  // 解放済みレバレッジ
  const unlockedLeverages = useMemo(
    () => getUnlockedLeverages(investmentData),
    [investmentData]
  );

  // 取引検証
  const validation = useMemo(() => {
    if (!selectedPair) return { valid: false };
    const margin = parseInt(marginInput) || 0;
    return validateOpenFXPosition(investmentData, gold, selectedPair.id, direction, margin, leverage);
  }, [selectedPair, marginInput, direction, leverage, investmentData, gold]);

  // 取引実行（サーバー優先）
  const handleOpenPosition = async () => {
    if (!validation.valid || !selectedPair || isLoading) return;

    const margin = parseInt(marginInput) || 0;
    const options = {
      stopLoss: stopLoss ? parseFloat(stopLoss) : null,
      takeProfit: takeProfit ? parseFloat(takeProfit) : null,
    };

    setIsLoading(true);
    setErrorMessage(null);

    try {
      // サーバー保存を先に実行
      const serverResult = await saveInvestmentTradeToServer({
        action: 'open_fx',
        pair: selectedPair.id,
        margin: margin,
        leverage,
        direction,
        stop_loss: options.stopLoss,
        take_profit: options.takeProfit,
      });

      // 未認証の場合はローカルのみで処理
      if (serverResult.error === 'Not authenticated') {
        console.log('[FXTrading] Not authenticated, proceeding with local only');
      } else if (!serverResult.success) {
        // サーバーエラーの場合は処理を中止
        setErrorMessage(`サーバー保存に失敗しました: ${serverResult.error}`);
        return;
      } else {
        setSelectedPair(null); setTradeMode('positions'); setMarginInput('10000'); setStopLoss(''); setTakeProfit(''); return;
      }

      // ローカル処理
      const result = openFXPosition(
        investmentData,
        selectedPair.id,
        direction,
        margin,
        leverage,
        currentBattleNumber,
        marketIndex,
        marketState,
        options
      );

      updatePlayerData({
        ...playerData,
        gold: gold - result.cost,
        investmentData: result.newInvestmentData,
      });

      // リセット
      setSelectedPair(null);
      setTradeMode('positions');
      setMarginInput('10000');
      setStopLoss('');
      setTakeProfit('');
    } finally {
      setIsLoading(false);
    }
  };

  // ポジション決済（サーバー優先）
  const handleClosePosition = async (positionId) => {
    if (isLoading) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      // サーバー保存を先に実行
      const serverResult = await saveInvestmentTradeToServer({
        action: 'close_fx',
        position_id: positionId,
      });

      // 未認証の場合はローカルのみで処理
      if (serverResult.error === 'Not authenticated') {
        console.log('[FXTrading] Not authenticated, proceeding with local only');
      } else if (!serverResult.success) {
        // サーバーエラーの場合は処理を中止
        setErrorMessage(`サーバー保存に失敗しました: ${serverResult.error}`);
        return;
      } else {
        return;
      }

      // ローカル処理
      const result = closeFXPosition(investmentData, positionId, currentBattleNumber, marketIndex, marketState);

      updatePlayerData({
        ...playerData,
        gold: gold + result.returnAmount,
        investmentData: result.newInvestmentData,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: '#1a1a2e',
      color: '#e0e0e0',
    },
    header: {
      padding: isMobile ? '12px' : '16px',
      borderBottom: '1px solid #333',
      backgroundColor: '#16213e',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: 'bold',
      color: '#ff9800',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    backButton: {
      padding: '8px 16px',
      backgroundColor: '#333',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #333',
    },
    tab: {
      flex: 1,
      padding: '12px',
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderBottom: '2px solid transparent',
      backgroundColor: 'transparent',
      color: '#888',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    tabActive: {
      color: '#ff9800',
      borderBottom: '2px solid #ff9800',
      backgroundColor: 'rgba(255, 152, 0, 0.1)',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px',
    },
    pairList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    pairCard: {
      display: 'flex',
      alignItems: 'center',
      padding: isMobile ? '12px' : '16px',
      backgroundColor: '#16213e',
      borderRadius: '8px',
      cursor: 'pointer',
      border: '1px solid transparent',
      transition: 'all 0.2s',
    },
    pairSymbol: {
      fontSize: isMobile ? '20px' : '24px',
      marginRight: '12px',
    },
    pairInfo: {
      flex: 1,
    },
    pairName: {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    pairRisk: {
      fontSize: '11px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    pairRate: {
      textAlign: 'right',
    },
    rateValue: {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: 'bold',
    },
    rateChange: {
      fontSize: '12px',
    },
    tradeForm: {
      backgroundColor: '#16213e',
      borderRadius: '8px',
      padding: isMobile ? '16px' : '24px',
    },
    formTitle: {
      fontSize: '16px',
      fontWeight: 'bold',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    formGroup: {
      marginBottom: '16px',
    },
    formLabel: {
      display: 'block',
      fontSize: '12px',
      color: '#888',
      marginBottom: '8px',
    },
    directionButtons: {
      display: 'flex',
      gap: '8px',
    },
    directionButton: {
      flex: 1,
      padding: '12px',
      border: '2px solid #333',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: 'transparent',
      color: '#888',
    },
    directionButtonLong: {
      border: '2px solid #4caf50',
      backgroundColor: 'rgba(76, 175, 80, 0.2)',
      color: '#4caf50',
    },
    directionButtonShort: {
      border: '2px solid #f44336',
      backgroundColor: 'rgba(244, 67, 54, 0.2)',
      color: '#f44336',
    },
    input: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#0f3460',
      border: '1px solid #333',
      borderRadius: '4px',
      color: '#fff',
      fontSize: '14px',
    },
    leverageButtons: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
    },
    leverageButton: {
      padding: '8px 16px',
      border: '1px solid #333',
      borderRadius: '4px',
      cursor: 'pointer',
      backgroundColor: 'transparent',
      color: '#888',
      transition: 'all 0.2s',
    },
    leverageButtonActive: {
      border: '1px solid #ff9800',
      backgroundColor: 'rgba(255, 152, 0, 0.2)',
      color: '#ff9800',
    },
    leverageButtonDisabled: {
      opacity: 0.3,
      cursor: 'not-allowed',
    },
    summary: {
      backgroundColor: '#0f3460',
      padding: '12px',
      borderRadius: '4px',
      marginBottom: '16px',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '4px',
      fontSize: '13px',
    },
    executeButton: {
      width: '100%',
      padding: '16px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: '16px',
      backgroundColor: '#ff9800',
      color: '#fff',
    },
    executeButtonDisabled: {
      backgroundColor: '#333',
      cursor: 'not-allowed',
    },
    positionCard: {
      backgroundColor: '#16213e',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
    },
    positionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px',
    },
    positionPair: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    positionDirection: {
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 'bold',
    },
    positionDirectionLong: {
      backgroundColor: 'rgba(76, 175, 80, 0.2)',
      color: '#4caf50',
    },
    positionDirectionShort: {
      backgroundColor: 'rgba(244, 67, 54, 0.2)',
      color: '#f44336',
    },
    positionDetails: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '8px',
      marginBottom: '12px',
    },
    positionDetail: {
      fontSize: '12px',
    },
    positionDetailLabel: {
      color: '#888',
    },
    positionDetailValue: {
      fontWeight: 'bold',
    },
    positionPnL: {
      textAlign: 'center',
      padding: '12px',
      borderRadius: '4px',
      marginBottom: '12px',
    },
    positionPnLPositive: {
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      color: '#4caf50',
    },
    positionPnLNegative: {
      backgroundColor: 'rgba(244, 67, 54, 0.1)',
      color: '#f44336',
    },
    closeButton: {
      width: '100%',
      padding: '12px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      backgroundColor: '#f44336',
      color: '#fff',
      fontWeight: 'bold',
    },
    warningBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '4px',
      backgroundColor: 'rgba(255, 152, 0, 0.2)',
      color: '#ff9800',
      fontSize: '11px',
    },
    emptyMessage: {
      textAlign: 'center',
      padding: '40px',
      color: '#888',
    },
    infoNote: {
      backgroundColor: 'rgba(100, 100, 100, 0.2)',
      border: '1px solid #444',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '12px',
    },
    infoNoteTitle: {
      fontSize: '13px',
      fontWeight: 'bold',
      color: '#aaa',
      marginBottom: '6px',
    },
    infoNoteText: {
      fontSize: '12px',
      color: '#888',
      lineHeight: '1.5',
    },
  };

  // ペア選択時の取引フォーム
  const renderTradeForm = () => {
    if (!selectedPair) return null;

    const margin = parseInt(marginInput) || 0;
    const positionValue = margin * leverage;
    const liquidationPercent = (100 / leverage) * 0.5;

    return (
      <div style={styles.tradeForm}>
        <div style={styles.formTitle}>
          <span>{selectedPair.symbol}</span>
          <span>{selectedPair.name}</span>
          <button
            style={{ marginLeft: 'auto', ...styles.backButton }}
            onClick={() => {
              setSelectedPair(null);
              setTradeMode('list');
            }}
          >
            戻る
          </button>
        </div>

        {/* リスク警告 */}
        {selectedPair.warning && (
          <div style={{ ...styles.warningBadge, marginBottom: '16px', width: '100%', justifyContent: 'center' }}>
            ⚠️ このペアは高ボラティリティです（推奨レバレッジ: 最大{selectedPair.maxLeverage}倍）
          </div>
        )}

        {/* 方向選択 */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>取引方向</label>
          <div style={styles.directionButtons}>
            <button
              style={{
                ...styles.directionButton,
                ...(direction === 'long' ? styles.directionButtonLong : {}),
              }}
              onClick={() => setDirection('long')}
            >
              📈 ロング（買い）
            </button>
            <button
              style={{
                ...styles.directionButton,
                ...(direction === 'short' ? styles.directionButtonShort : {}),
              }}
              onClick={() => setDirection('short')}
            >
              📉 ショート（売り）
            </button>
          </div>
        </div>

        {/* 証拠金 */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>証拠金（所持金: {gold.toLocaleString()}G）</label>
          <input
            type="number"
            style={styles.input}
            value={marginInput}
            onChange={(e) => setMarginInput(e.target.value)}
            min="1000"
            step="1000"
          />
        </div>

        {/* レバレッジ */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>レバレッジ</label>
          <div style={styles.leverageButtons}>
            {INVESTMENT.LEVERAGE_OPTIONS.map((lev) => {
              const isUnlocked = unlockedLeverages.includes(lev);
              const isActive = leverage === lev;
              const requiredExp = INVESTMENT.LEVERAGE_UNLOCK[lev] || 0;
              const currentExp = investmentData.experienceCount || 0;
              const remaining = requiredExp - currentExp;
              return (
                <button
                  key={lev}
                  style={{
                    ...styles.leverageButton,
                    ...(isActive ? styles.leverageButtonActive : {}),
                    ...(!isUnlocked ? styles.leverageButtonDisabled : {}),
                  }}
                  onClick={() => isUnlocked && setLeverage(lev)}
                  disabled={!isUnlocked}
                  title={!isUnlocked ? `あと${remaining}回投資で解放` : `${lev}倍レバレッジ`}
                >
                  {lev}x
                  {!isUnlocked && <span style={{ fontSize: '10px', display: 'block' }}>🔒</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* 自動注文（任意） */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>ストップロス（任意: -◯%で損切り）</label>
          <input
            type="number"
            style={styles.input}
            value={stopLoss}
            onChange={(e) => setStopLoss(e.target.value)}
            placeholder="例: -10"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>テイクプロフィット（任意: +◯%で利確）</label>
          <input
            type="number"
            style={styles.input}
            value={takeProfit}
            onChange={(e) => setTakeProfit(e.target.value)}
            placeholder="例: 20"
          />
        </div>

        {/* サマリー */}
        <div style={styles.summary}>
          <div style={styles.summaryRow}>
            <span>現在レート</span>
            <span>{selectedPair.currentRate.toFixed(4)}</span>
          </div>
          <div style={styles.summaryRow}>
            <span>実質ポジション</span>
            <span>{positionValue.toLocaleString()}G</span>
          </div>
          <div style={styles.summaryRow}>
            <span>スプレッド（0.5%）</span>
            <span>{validation.spread?.toLocaleString() || '-'}G</span>
          </div>
          <div style={styles.summaryRow}>
            <span>合計コスト</span>
            <span style={{ fontWeight: 'bold' }}>{validation.cost?.toLocaleString() || '-'}G</span>
          </div>
          <div style={styles.summaryRow}>
            <span>ロスカットライン</span>
            <span style={{ color: '#f44336' }}>-{liquidationPercent.toFixed(1)}%</span>
          </div>
        </div>

        {/* エラーメッセージ */}
        {!validation.valid && validation.error && (
          <div style={{ color: '#f44336', marginBottom: '16px', fontSize: '14px' }}>
            ⚠️ {validation.error}
          </div>
        )}

        {/* 警告メッセージ（取引可能） */}
        {validation.valid && validation.warning && (
          <div style={{ color: '#ff9800', marginBottom: '16px', fontSize: '14px', background: 'rgba(255, 152, 0, 0.1)', padding: '8px', borderRadius: '4px' }}>
            {validation.warning}
          </div>
        )}

        {/* エラーメッセージ */}
        {errorMessage && (
          <div style={{ color: '#f44336', marginBottom: '16px', fontSize: '14px' }}>
            ⚠️ {errorMessage}
          </div>
        )}

        {/* 取引実行ボタン */}
        <button
          style={{
            ...styles.executeButton,
            ...(isLoading || !validation.valid ? styles.executeButtonDisabled : {}),
          }}
          onClick={handleOpenPosition}
          disabled={isLoading || !validation.valid}
        >
          {isLoading ? '処理中...' : '取引実行'}
        </button>
      </div>
    );
  };

  // ペア一覧
  const renderPairList = () => (
    <div style={styles.pairList}>
      {/* 説明注釈 */}
      <div style={styles.infoNote}>
        <div style={styles.infoNoteTitle}>📊 FXレートについて</div>
        <div style={styles.infoNoteText}>
          レートは2属性間のカード平均価格の比率です。
          カード市場のニュースが両属性に影響するため、FXレートの変動はカード価格より緩やかになります。
        </div>
      </div>
      {pairs.map((pair) => (
        <div
          key={pair.id}
          style={styles.pairCard}
          onClick={() => {
            setSelectedPair(pair);
            setTradeMode('trade');
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#ff9800';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'transparent';
          }}
        >
          <div style={styles.pairSymbol}>{pair.symbol}</div>
          <div style={styles.pairInfo}>
            <div style={styles.pairName}>{pair.name}</div>
            <div style={{
              ...styles.pairRisk,
              color: pair.warning ? '#ff9800' : '#888',
            }}>
              <span>{pair.stars}</span>
              <span>{pair.level}</span>
              {pair.positionCount > 0 && (
                <span style={{
                  marginLeft: '8px',
                  padding: '2px 6px',
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  borderRadius: '4px',
                  color: '#4caf50',
                }}>
                  {pair.positionCount}ポジション
                </span>
              )}
            </div>
          </div>
          <div style={styles.pairRate}>
            <div style={styles.rateValue}>{pair.currentRate.toFixed(4)}</div>
            <div style={{
              ...styles.rateChange,
              color: pair.rateChange > 0 ? '#4caf50' : pair.rateChange < 0 ? '#f44336' : '#888',
            }}>
              {pair.rateChange > 0 ? '+' : ''}{pair.rateChange.toFixed(2)}%
              {pair.isHot && ' 🔥'}
              {pair.isCold && ' ❄️'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // ポジション一覧
  const renderPositions = () => {
    if (positions.length === 0) {
      return (
        <div style={styles.emptyMessage}>
          📊 ポジションがありません
        </div>
      );
    }

    return (
      <div>
        {positions.map((pos) => (
          <div key={pos.id} style={styles.positionCard}>
            <div style={styles.positionHeader}>
              <div style={styles.positionPair}>
                <span style={{ fontSize: '20px' }}>{pos.pair.symbol}</span>
                <span style={{ fontWeight: 'bold' }}>{pos.pair.name}</span>
                <span style={{
                  ...styles.positionDirection,
                  ...(pos.direction === 'long' ? styles.positionDirectionLong : styles.positionDirectionShort),
                }}>
                  {pos.direction === 'long' ? 'ロング' : 'ショート'} ×{pos.leverage}
                </span>
              </div>
              {pos.isNearLiquidation && (
                <span style={styles.warningBadge}>⚠️ ロスカット注意</span>
              )}
            </div>

            <div style={styles.positionDetails}>
              <div style={styles.positionDetail}>
                <div style={styles.positionDetailLabel}>証拠金</div>
                <div style={styles.positionDetailValue}>{pos.margin.toLocaleString()}G</div>
              </div>
              <div style={styles.positionDetail}>
                <div style={styles.positionDetailLabel}>エントリー</div>
                <div style={styles.positionDetailValue}>{pos.entryRate.toFixed(4)}</div>
              </div>
              <div style={styles.positionDetail}>
                <div style={styles.positionDetailLabel}>現在レート</div>
                <div style={styles.positionDetailValue}>{pos.currentRate.toFixed(4)}</div>
              </div>
              <div style={styles.positionDetail}>
                <div style={styles.positionDetailLabel}>経過日数</div>
                <div style={styles.positionDetailValue}>{pos.daysOpen}日</div>
              </div>
            </div>

            {/* 自動注文表示 */}
            {(pos.stopLoss !== null || pos.takeProfit !== null) && (
              <div style={{ marginBottom: '12px', fontSize: '12px', color: '#888' }}>
                {pos.stopLoss !== null && <span>🛡️ SL: {pos.stopLoss}% </span>}
                {pos.takeProfit !== null && <span>🎯 TP: +{pos.takeProfit}%</span>}
              </div>
            )}

            <div style={{
              ...styles.positionPnL,
              ...(pos.pnl >= 0 ? styles.positionPnLPositive : styles.positionPnLNegative),
            }}>
              <div style={{ fontSize: '12px', marginBottom: '4px' }}>含み損益</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {pos.pnl >= 0 ? '+' : ''}{pos.pnl.toLocaleString()}G
                ({pos.pnlPercent >= 0 ? '+' : ''}{pos.pnlPercent.toFixed(1)}%)
              </div>
            </div>

            <button
              style={{
                ...styles.closeButton,
                ...(isLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
              }}
              onClick={() => handleClosePosition(pos.id)}
              disabled={isLoading}
            >
              {isLoading ? '処理中...' : `決済する（返還: ${pos.estimatedValue.toLocaleString()}G）`}
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>
          <span>📈</span>
          <span>属性レート取引（FX）</span>
        </div>
      </div>

      {/* タブ */}
      <div style={styles.tabs}>
        <button
          style={{
            ...styles.tab,
            ...(tradeMode === 'list' || tradeMode === 'trade' ? styles.tabActive : {}),
          }}
          onClick={() => {
            setTradeMode('list');
            setSelectedPair(null);
          }}
        >
          レート一覧
        </button>
        <button
          style={{
            ...styles.tab,
            ...(tradeMode === 'positions' ? styles.tabActive : {}),
          }}
          onClick={() => setTradeMode('positions')}
        >
          ポジション ({positions.length})
        </button>
      </div>

      <div style={styles.content}>
        {tradeMode === 'list' && renderPairList()}
        {tradeMode === 'trade' && renderTradeForm()}
        {tradeMode === 'positions' && renderPositions()}
      </div>
    </div>
  );
};

export default FXTrading;
